import React from "react";

import WordButton from "../WordButton";

import styles from "./GameGrid.module.css";

import { useSpring, animated } from "react-spring";
import { PuzzleDataContext } from "../../providers/PuzzleData";
import { GameStatusContext } from "../../providers/GameStatus";

import { Popover, PopoverTrigger } from "../ui/popover";
import { Badge } from "../ui/badge";

function WordRow({ words }) {
    return (
        <div className={`grid grid-cols-4 gap-2 sm:gap-3 md:gap-4`}>
            {words.map((word) => (
                <WordButton key={word} word={word} fullCandidateSize={4} />
            ))}
        </div>
    );
}

export function SolvedWordRow({ ...props }) {
    const DIFFICULTY_COLOR_MAP = {
        1: "#f9dc5c", // yellow - warmer
        2: "#78c788", // green - darker contrast
        3: "#6cb4ee", // blue - richer
        4: "#ba9bc8", // purple - more saturated
    };

    const color = `${DIFFICULTY_COLOR_MAP[props.difficulty]}`;

    const [hasBeenClicked, setHasBeenClicked] = React.useState(false);

    const springProps = useSpring({
        from: {
            opacity: 0,
            transform: "translateY(-20px) scale(0.95)",
        },
        to: {
            opacity: 1,
            transform: "translateY(0px) scale(1)",
        },
        config: { mass: 1, tension: 200, friction: 24 },
    });
    // if there is an image available render it as a popover
    const isImageAvailable = props.imageSrc != null;
    return (
        <animated.div style={springProps}>
            {!isImageAvailable ? (
                <div style={{ backgroundColor: color, borderRadius: 12 }} className="py-2 sm:py-3 md:py-4">
                    <p className="text-center font-bold pb-2 text-black text-base sm:text-lg md:text-xl uppercase tracking-wide">{props.category}</p>
                    <p className="text-center font-medium px-4 text-black text-sm sm:text-base md:text-lg">{props.words.join(", ")}</p>
                </div>
            ) : (
                <Popover>
                    <PopoverTrigger asChild>
                        <div
                            className="cursor-pointer hover:animate-pulse shadow-md py-2 sm:py-3 md:py-4"
                            style={{ backgroundColor: color, borderRadius: 12 }}
                            onClick={() => setHasBeenClicked(true)}
                        >
                            {!hasBeenClicked && (
                                <Badge className="animate-pulse absolute top-0 right-0 mr-2 mt-2">
                                    View More
                                </Badge>
                            )}
                            <p className="text-center font-bold pb-2 text-black text-base sm:text-lg md:text-xl uppercase tracking-wide">{props.category}</p>
                            <p className="text-center font-medium px-4 text-black text-sm sm:text-base md:text-lg">{props.words.join(", ")}</p>
                        </div>
                    </PopoverTrigger>
                </Popover>
            )}
        </animated.div>
    );
}

function GameGrid({ gameRows, shouldGridShake, setShouldGridShake }) {
    const { submittedGuesses, isGameOver, isGameWon, solvedGameData } =
        React.useContext(GameStatusContext);

    const { gameData } = React.useContext(PuzzleDataContext);

    React.useEffect(() => {
        const shakeEffect = window.setTimeout(() => {
            setShouldGridShake(false);
            // this timeout should probably be calculated since it depends on animation values for the grid shake
        }, 2000);

        // cleanup timeout
        return () => window.clearTimeout(shakeEffect);
    }, [submittedGuesses, setShouldGridShake]);

    const isGameOverAndLost = isGameOver && !isGameWon;
    const isGameOverAndWon = isGameOver && isGameWon;
    const isGameActive = !isGameOver;
    const isGameActiveWithAnySolvedRows =
        isGameActive && solvedGameData.length > 0;

    return (
        <div>
            {(isGameOverAndWon || isGameActiveWithAnySolvedRows) && (
                <div className="grid gap-y-2 sm:gap-y-3 md:gap-y-4 mb-2 sm:mb-3 md:mb-4">
                    {solvedGameData.map((solvedRowObj) => (
                        <SolvedWordRow key={solvedRowObj.category} {...solvedRowObj} />
                    ))}
                </div>
            )}
            {isGameActive && (
                <div className={`grid gap-y-2 sm:gap-y-3 md:gap-y-4 ${shouldGridShake ? styles.shake : ""}`}>
                    {gameRows.map((row, idx) => (
                        <WordRow key={idx} words={row} />
                    ))}
                </div>
            )}
            {/* Show correct answers here after the game is over if they lost */}
            {isGameOverAndLost && (
                <div className="grid gap-y-2 sm:gap-y-3 md:gap-y-4 pb-2">
                    <p className="text-base sm:text-lg text-slate-200 mb-2">The answer categories are below.</p>
                    {gameData.map((obj) => (
                        <SolvedWordRow key={obj.category} {...obj} />
                    ))}
                </div>
            )}
        </div>
    );
}

export default GameGrid;
